import { FC, useMemo, useState } from 'react';
import {
  AdvancedPagination,
  Button,
  Card,
  DateRangePicker,
  Divider,
  InputSelect,
  RadixTable,
  TColumn,
} from '@features/ui';
import {
  accountStatusOption,
  maxDailyProfitAllowedOption,
  minTradingDayOptions,
} from '@features/admin/reports/reportData.ts';
import { DateValueType, formatCurrency } from '@lib/utils.ts';
import { TPaDailyPlHitting30PercentOfTargetTable } from '@src/types/reports';
import { useNavigate } from 'react-router-dom';
import { AccountStatsDialog } from '@features/admin/reports/components/AccountStatsDialog';

interface PaDailyPlHitting30PercentOfTargetTableProps {
  accountStatus: string;
  setAccountStatus: (value: string) => void;
  profitPercent: string;
  setProfitPercent: (value: string) => void;
  minTradingDays: string;
  setMinTradingDays: (value: string) => void;
  startDate: DateValueType;
  setStartDate: (value: DateValueType) => void;
  data: TPaDailyPlHitting30PercentOfTargetTable[];
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
  onGetData: () => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const renderPayoutCell = (count?: number, total?: number) => {
  if (!count || count <= 0) {
    return '-';
  }

  return (
    <div className={'flex flex-col gap-1'}>
      <p>${formatCurrency(total ?? 0, 2)}</p>
      <p>{count} payouts</p>
    </div>
  );
};

export const PaDailyPlHitting30PercentOfTargetTable: FC<
  PaDailyPlHitting30PercentOfTargetTableProps
> = ({
  accountStatus,
  setAccountStatus,
  profitPercent,
  setProfitPercent,
  minTradingDays,
  setMinTradingDays,
  startDate,
  setStartDate,
  data,
  currentPage,
  pageSize,
  totalCount,
  totalPages,
  isLoading,
  onGetData,
  onPageChange,
  onPageSizeChange,
}) => {
  const navigate = useNavigate();
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] =
    useState<TPaDailyPlHitting30PercentOfTargetTable | null>(null);

  const selectedAccountId = selectedAccount?.id
    ? Number(selectedAccount.id)
    : null;

  const validSelectedAccountId =
    selectedAccountId !== null && !Number.isNaN(selectedAccountId)
      ? selectedAccountId
      : null;

  const columns = useMemo(
    (): TColumn<TPaDailyPlHitting30PercentOfTargetTable>[] => [
      {
        title: 'User',
        render: (item) => (
          <div className={'flex flex-col gap-1'}>
            <button
              className={'w-fit text-[#3490FF] underline text-left cursor-pointer'}
              type={'button'}
              onClick={() => navigate(`/admin/users/${item?.userId ?? ''}`)}
            >
              {item?.userName}
            </button>
            <p>{item?.fullName}</p>
          </div>
        ),
      },
      {
        title: `Account No`,
        render: (item) => (
          <div className={'flex flex-col gap-1'}>
            <p>{item?.accountNo ?? ''}</p>
            <p>{item?.accountDes ?? ''}</p>
          </div>
        ),
      },
      {
        title: `Package`,
        render: (item) => <p>{item?.package ?? '-'}</p>,
      },
      {
        title: `Total Profit`,
        className: 'text-center',
        render: (item) => <p>${formatCurrency(item?.totalProfit ?? 0)}</p>,
      },
      {
        title: `Max Allowed Daily Profit`,
        className: 'text-center',
        render: (item) => (
          <p>${formatCurrency(item?.maxAllowedDailyProfit ?? 0, 2)}</p>
        ),
      },
      {
        title: `Highest Daily Profit`,
        className: 'text-center',
        render: (item) => (
          <p>${formatCurrency(item?.highestDailyProfit ?? 0)}</p>
        ),
      },
      {
        title: `Highest Daily Profit %`,
        className: 'text-center',
        render: (item) => <p>{item?.highestDailyProfitPercent ?? 0}%</p>,
      },
      {
        title: `Trading Days`,
        className: 'xl:pl-10 pl-2',
        render: (item) => (
          <div className={'flex flex-col gap-2 cursor-pointer'}>
            <button
              type={'button'}
              onClick={() => {
                setSelectedAccount(item ?? null);
                setStatsDialogOpen(true);
              }}
              className={
                'w-8 h-6.5 rounded-[3px] bg-lime flex items-center justify-center cursor-pointer'
              }
            >
              <p>{item?.tradingDays ?? 0}</p>
            </button>
          </div>
        ),
      },
      {
        title: `Last Payout`,
        className: 'text-center',
        render: (item) => <p>{item?.lastPayout ?? '-'}</p>,
      },
      {
        title: `Account Payouts`,
        className: 'text-center',
        render: (item) =>
          renderPayoutCell(item?.accountPayoutCount, item?.accountPayoutTotal),
      },
      {
        title: `Customer Payouts`,
        className: 'text-center',
        render: (item) =>
          renderPayoutCell(
            item?.customerPayoutCount,
            item?.customerPayoutTotal
          ),
      },
    ],
    [navigate]
  );

  return (
    <Card className={'mt-4.5'}>
      <div
        className={
          'flex lg:flex-row flex-col justify-between items-start mb-5 gap-6'
        }
      >
        <div className={'flex gap-6 flex-wrap'}>
          <div className={'flex flex-col gap-2'}>
            <p className={'text-xs text-[#919EAB]'}>Trading Start Date</p>
            <DateRangePicker
              asSingle={true}
              className={'max-w-[239px]'}
              placeholder={'Select start date'}
              rightIcon={<></>}
              size={'sm'}
              value={startDate}
              variant={'outline'}
              onChange={setStartDate}
            />
          </div>
          <div className={'flex flex-col gap-2'}>
            <p className={'text-xs text-[#919EAB]'}>
              Max Daily Profit Allowed %
            </p>
            <InputSelect
              className={'w-[239px] border-[#C6C6C6]'}
              options={maxDailyProfitAllowedOption}
              placeholder={'Select percentage'}
              value={profitPercent}
              onChange={setProfitPercent}
            />
          </div>
          <div className={'flex flex-col gap-2'}>
            <p className={'text-xs text-[#919EAB]'}>Min Trading Days</p>
            <InputSelect
              className={'w-[239px] border-[#C6C6C6]'}
              options={minTradingDayOptions}
              placeholder={'Select minimum days'}
              value={minTradingDays}
              onChange={setMinTradingDays}
            />
          </div>
          <div className={'flex flex-col gap-2'}>
            <p className={'text-xs text-[#919EAB]'}>Account Status</p>
            <InputSelect
              className={'w-[239px] border-[#C6C6C6]'}
              options={accountStatusOption}
              placeholder={'Select status'}
              value={accountStatus}
              onChange={setAccountStatus}
            />
          </div>
        </div>
        <div className={'flex gap-5 lg:mt-0 mt-2'}>
          <Button
            disabled={isLoading}
            size={'sm'}
            variant={'lime'}
            onClick={onGetData}
          >
            {isLoading ? 'Loading...' : 'Get'}
          </Button>
        </div>
      </div>
      <Divider className={'mb-1'} />
      <RadixTable columns={columns} data={data} />
      {totalCount > 0 ? (
        <AdvancedPagination
          className={'mt-6'}
          currentPage={currentPage}
          pageSize={pageSize}
          pageSizeOptions={[25, 50, 100, 250]}
          totalItems={totalCount}
          totalPages={totalPages}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      ) : null}

      <AccountStatsDialog
        accountId={validSelectedAccountId}
        accountNo={selectedAccount?.accountNo}
        open={statsDialogOpen}
        onOpenChange={setStatsDialogOpen}
      />
    </Card>
  );
};
